import { useId, useLayoutEffect, useRef, useState } from 'react';
import {
    computeBuyerPayouts,
    computeMultiBuyerPayouts,
    computeNodeEarnings,
} from './chainPayouts.js';
import {
    formatNumber,
    formatPercent,
    formatRial,
    nodeLabel,
} from './chainFormat';
import MultiBuyerPanel from './MultiBuyerPanel';
import NodeEarningsStack, { stackBottom } from './NodeEarningsStack';
import { BuyerDetails, NodeEarningsDetails } from './SingleBuyerDetails';
import './chain-graph.css';

const MODEL_COLORS = {
    halving: { primary: '#2563eb' },
    linear: { primary: '#059669' },
    gradual: { primary: '#d97706' },
};

const NODE_RADIUS = 25;
const NODE_STEP = 120;
const SVG_PADDING = 60;
const SINGLE_HEIGHT = 178;
const MULTI_MIN_HEIGHT = 100;

const sortedUnique = (values) => [...new Set(values)].sort((a, b) => a - b);

export default function ChainGraph({
    model,
    salesAmount,
    marginPercent,
    minMarginPercent,
}) {
    const depth = model?.levels?.length ?? 0;
    const [buyerIndex, setBuyerIndex] = useState(depth);
    const [multiMode, setMultiMode] = useState(false);
    const [buyers, setBuyers] = useState([]);
    const [activeTab, setActiveTab] = useState('buyer');
    const [animateDetails, setAnimateDetails] = useState(false);
    const nodeRefs = useRef([]);
    const graphScrollRef = useRef(null);
    const generatedId = useId().replaceAll(':', '');

    useLayoutEffect(() => {
        const graphScroll = graphScrollRef.current;
        if (!graphScroll) return undefined;

        const alignBuyerAtRight = () => {
            const maxScroll = graphScroll.scrollWidth - graphScroll.clientWidth;
            if (maxScroll > 8) graphScroll.scrollLeft = maxScroll;
        };
        alignBuyerAtRight();

        const resizeObserver = new ResizeObserver(alignBuyerAtRight);
        resizeObserver.observe(graphScroll);
        return () => resizeObserver.disconnect();
    }, [depth]);

    if (!model || !Array.isArray(model.levels) || model.levels.length === 0) {
        return null;
    }

    const nodeCount = depth + 1;
    const selectedBuyer = Math.min(buyerIndex, depth);
    const colors = MODEL_COLORS[model.distribution] ?? MODEL_COLORS.halving;

    // ----- what is shown: one buyer (classic) or several (stacks) -----
    const singleBuyer = multiMode
        ? (buyers.length === 1 ? buyers[0] : null)
        : selectedBuyer;
    const view = multiMode
        ? (buyers.length >= 2 ? 'multi' : buyers.length === 1 ? 'single' : 'none')
        : 'single';

    const buyerPayouts = singleBuyer === null
        ? null
        : computeBuyerPayouts(
            model,
            salesAmount,
            marginPercent,
            minMarginPercent,
            singleBuyer,
        );
    const multi = computeMultiBuyerPayouts(
        model,
        salesAmount,
        marginPercent,
        minMarginPercent,
        multiMode ? buyers : [],
    );
    const nodeEarnings = Array.from(
        { length: nodeCount },
        (_, index) => computeNodeEarnings(model, salesAmount, index),
    );

    // ----- geometry -----
    const positions = Array.from(
        { length: nodeCount },
        (_, index) => SVG_PADDING + index * NODE_STEP,
    );
    const viewBoxWidth = Math.max(NODE_STEP * depth + SVG_PADDING * 2, 720);
    const maxBoxes = Math.max(0, ...multi.perNode.map(({ boxes }) => boxes.length));
    const svgHeight = !multiMode
        ? SINGLE_HEIGHT
        : maxBoxes > 0
            ? stackBottom(maxBoxes) + 14
            : MULTI_MIN_HEIGHT;

    const markerBase = `chain-graph-arrow-${generatedId}`;
    const panelId = `chain-detail-panel-${generatedId}`;
    const buyerTabId = `chain-detail-buyer-tab-${generatedId}`;
    const earningsTabId = `chain-detail-earnings-tab-${generatedId}`;
    const multiTabId = `chain-detail-multi-tab-${generatedId}`;

    const visibleTab = view === 'multi' ? 'multi' : activeTab;
    const isSafe = buyerPayouts ? buyerPayouts.isSafe : multi.isSafe;
    const currentLabel = singleBuyer === null ? '' : nodeLabel(singleBuyer);

    // ----- interaction -----
    const toggleMultiBuyer = (index) => {
        const next = !multiMode
            ? sortedUnique([buyerIndex, index])
            : buyers.includes(index)
                ? buyers.filter((buyer) => buyer !== index)
                : sortedUnique([...buyers, index]);
        setBuyers(next);
        setMultiMode(true);
    };

    const activateNode = (index, animate, additive = false) => {
        setAnimateDetails(animate);
        if (multiMode || additive) {
            toggleMultiBuyer(index);
            return;
        }
        setBuyerIndex(index);
    };

    const toggleMultiMode = () => {
        setAnimateDetails(true);
        if (multiMode) {
            setBuyerIndex(buyers[0] ?? selectedBuyer);
            setMultiMode(false);
        } else {
            setBuyers([selectedBuyer]);
            setMultiMode(true);
        }
    };

    const selectAllBuyers = () => {
        setAnimateDetails(true);
        setBuyers(Array.from({ length: nodeCount }, (_, index) => index));
    };

    const clearBuyers = () => {
        setAnimateDetails(true);
        setBuyers([]);
    };

    const handleNodeKeyDown = (event, index) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            activateNode(index, false, event.ctrlKey || event.metaKey || event.shiftKey);
            return;
        }

        // RTL page: ArrowLeft moves toward the root (A), ArrowRight toward the buyer end.
        const nextIndex = event.key === 'ArrowLeft'
            ? Math.max(0, index - 1)
            : event.key === 'ArrowRight'
                ? Math.min(depth, index + 1)
                : index;
        if (nextIndex === index) return;

        event.preventDefault();
        if (!multiMode) {
            setAnimateDetails(false);
            setBuyerIndex(nextIndex);
        }
        nodeRefs.current[nextIndex]?.focus();
    };

    const selectTab = (event, tab) => {
        setAnimateDetails(event.detail > 0);
        setActiveTab(tab);
    };

    // ----- header stats -----
    const stats = view === 'single'
        ? [
            { label: 'خریدار انتخاب‌شده', value: `گره ${currentLabel}` },
            { label: 'پورسانت این خرید', value: formatRial(buyerPayouts.totalCommissionAmount) },
            { label: 'درصد پورسانت', value: formatPercent(buyerPayouts.totalCommissionPercent) },
            {
                label: 'سود باقی‌مانده',
                value: formatPercent(buyerPayouts.remainingProfitPercent),
                tone: buyerPayouts.isSafe ? 'text-success' : 'text-danger',
            },
        ]
        : view === 'multi'
            ? [
                {
                    label: 'خریداران انتخاب‌شده',
                    value: multi.purchases.map(({ label }) => label).join('، '),
                },
                { label: 'کل پورسانت خریدها', value: formatRial(multi.totalCommissionAmount) },
                { label: 'سهم پلتفرم (سطوح پرداخت‌نشده)', value: formatRial(multi.platformKeptAmount) },
                {
                    label: 'سود باقی‌مانده (مجموع)',
                    value: formatRial(multi.remainingProfitAmount),
                    tone: multi.isSafe ? 'text-success' : 'text-danger',
                },
            ]
            : [{ label: 'خریدار انتخاب‌شده', value: 'هیچ‌کدام' }];

    const roleLabel = (index, isBuyer, receives) => {
        if (isBuyer) return multiMode && receives ? 'خریدار و دریافت‌کننده' : 'خریدار';
        return index === 0 ? 'ریشه' : 'معرف';
    };

    const nodeAriaLabel = (index, isBuyer) => {
        const label = nodeLabel(index);
        if (multiMode) {
            return isBuyer
                ? `گره ${label}، در میان خریداران انتخاب‌شده`
                : `گره ${label}، انتخاب‌نشده، برای افزودن به خریداران کلیک کنید`;
        }
        return `گره ${label}، سطح ${formatNumber(index + 1)} از بالا، برای انتخاب به‌عنوان خریدار کلیک کنید`;
    };

    return (
        <section
            className="chain-graph"
            style={{ '--chain-accent': colors.primary }}
            aria-label={`مدل ${model.label}`}
        >
            <header className="chain-graph-header">
                <div className="chain-graph-title-row">
                    <span className="chain-graph-color-dot" aria-hidden="true" />
                    <h3>{model.label}</h3>
                    <span className={`chain-graph-status ${isSafe ? 'safe' : 'danger'}`}>
                        {isSafe ? '✅ امن' : '❌ خطر'}
                    </span>
                </div>
                <div className="chain-graph-stats">
                    {stats.map(({ label, value, tone }) => (
                        <div className="chain-stat" key={label}>
                            <span>{label}</span>
                            <strong className={tone}>{value}</strong>
                        </div>
                    ))}
                </div>
            </header>

            <div className="chain-graph-controls">
                <button
                    className={`chain-graph-multi-toggle ${multiMode ? 'is-active' : ''}`}
                    type="button"
                    aria-pressed={multiMode}
                    onClick={toggleMultiMode}
                >
                    {multiMode ? 'پایان انتخاب چند خریدار' : 'انتخاب چند خریدار'}
                </button>
                {multiMode && (
                    <div className="chain-graph-selection-actions">
                        <span>{formatNumber(buyers.length)} خریدار انتخاب شده</span>
                        <button type="button" onClick={selectAllBuyers}>
                            انتخاب همه خریداران
                        </button>
                        <button type="button" onClick={clearBuyers}>
                            پاک کردن انتخاب
                        </button>
                    </div>
                )}
            </div>

            <div
                ref={graphScrollRef}
                className="chain-graph-scroll"
                role="group"
                aria-label="زنجیره معرف‌ها"
            >
                <svg
                    className="chain-graph-svg"
                    viewBox={`0 0 ${viewBoxWidth} ${svgHeight}`}
                    preserveAspectRatio="xMidYMid meet"
                    style={{ minWidth: `${viewBoxWidth}px` }}
                    role="group"
                    aria-label="گره‌های زنجیره، از ریشه در چپ تا خریدار در راست"
                >
                    <defs>
                        {['default', 'active', 'muted'].map((state) => (
                            <marker
                                key={state}
                                id={`${markerBase}-${state}`}
                                className={`chain-graph-marker is-${state}`}
                                viewBox="0 0 10 10"
                                refX="9"
                                refY="5"
                                markerWidth="6"
                                markerHeight="6"
                                orient="auto"
                                markerUnits="userSpaceOnUse"
                            >
                                <path d="M 0 0 L 10 5 L 0 10 z" />
                            </marker>
                        ))}
                    </defs>

                    {positions.slice(1).map((position, index) => {
                        // Connector `index` joins node index and node index+1.
                        const state = !multiMode
                            ? 'default'
                            : buyers.some((buyer) => buyer >= index + 1)
                                ? 'active'
                                : 'muted';
                        return (
                            <line
                                key={`connector-${index}`}
                                className={`chain-graph-connector is-${state}`}
                                x1={position - NODE_RADIUS}
                                y1="35"
                                x2={positions[index] + NODE_RADIUS}
                                y2="35"
                                markerEnd={`url(#${markerBase}-${state})`}
                            />
                        );
                    })}

                    {positions.map((position, index) => {
                        const label = nodeLabel(index);
                        const stack = multiMode ? multi.perNode[index] : null;
                        const receives = Boolean(stack && stack.boxes.length > 0);
                        const isBuyer = multiMode
                            ? buyers.includes(index)
                            : index === selectedBuyer;
                        const isPaid = multiMode ? receives : index < selectedBuyer;
                        const singlePayout = !multiMode && buyerPayouts
                            ? buyerPayouts.paidUplines.find(({ nodeIndex }) => nodeIndex === index)
                            : undefined;

                        return (
                            <g
                                key={`node-${index}`}
                                ref={(element) => {
                                    nodeRefs.current[index] = element;
                                }}
                                className={[
                                    'chain-graph-node',
                                    isBuyer ? 'is-buyer' : '',
                                    isPaid ? 'is-paid' : 'is-inactive',
                                ].filter(Boolean).join(' ')}
                                tabIndex={0}
                                role="button"
                                aria-pressed={isBuyer}
                                aria-label={nodeAriaLabel(index, isBuyer)}
                                onClick={(event) => activateNode(
                                    index,
                                    event.detail > 0,
                                    event.ctrlKey || event.metaKey || event.shiftKey,
                                )}
                                onKeyDown={(event) => handleNodeKeyDown(event, index)}
                            >
                                <circle cx={position} cy="35" r={NODE_RADIUS} />
                                <text
                                    className="chain-graph-node-label"
                                    x={position}
                                    y="40"
                                    textAnchor="middle"
                                >
                                    {label}
                                </text>
                                {multiMode && isBuyer && (
                                    <text
                                        className="chain-graph-check"
                                        x={position + 19}
                                        y="16"
                                        textAnchor="middle"
                                        aria-hidden="true"
                                    >
                                        ✓
                                    </text>
                                )}
                                <text
                                    className="chain-graph-node-role"
                                    x={position}
                                    y="73"
                                    textAnchor="middle"
                                    direction="rtl"
                                >
                                    {roleLabel(index, isBuyer, receives)}
                                </text>

                                {singlePayout && (
                                    <>
                                        <g className="chain-graph-payout" aria-hidden="true">
                                            <rect x={position - 54} y="82" width="108" height="46" rx="8" />
                                            <text
                                                className="chain-graph-payout-percent"
                                                x={position}
                                                y="101"
                                                textAnchor="middle"
                                            >
                                                {formatPercent(singlePayout.percent)}
                                            </text>
                                            <text
                                                className="chain-graph-payout-amount"
                                                x={position}
                                                y="119"
                                                textAnchor="middle"
                                                direction="rtl"
                                            >
                                                {formatRial(singlePayout.amount)}
                                            </text>
                                        </g>
                                        <text
                                            className="chain-graph-level"
                                            x={position}
                                            y="151"
                                            textAnchor="middle"
                                            direction="rtl"
                                        >
                                            سطح {formatNumber(singlePayout.level)}
                                        </text>
                                    </>
                                )}

                                {stack && (
                                    <NodeEarningsStack
                                        x={position}
                                        boxes={stack.boxes}
                                        total={stack.total}
                                    />
                                )}
                            </g>
                        );
                    })}
                </svg>
            </div>

            {multiMode && buyers.length === 0 && (
                <p className="chain-graph-hint">
                    روی گره‌ها کلیک کنید تا خریداران را انتخاب کنید.
                </p>
            )}
            {multiMode && buyers.includes(0) && (
                <p className="chain-graph-hint">
                    گره A بالاترین جایگاه است؛ خرید آن پورسانتی پرداخت نمی‌کند، پس برای
                    آن جعبه‌ای نمایش داده نمی‌شود.
                </p>
            )}

            <p className="chain-graph-rounding-note">
                پرداخت واقعی هر سطح به ریال کامل گرد می‌شود؛ جمع درصدی ممکن است با
                جمع ردیف‌ها یک ریال اختلاف داشته باشد.
            </p>

            <section className="chain-earning-summary" aria-labelledby={`${panelId}-summary-title`}>
                <h4 id={`${panelId}-summary-title`}>
                    درآمد هر جایگاه
                    <span>اگر همهٔ پایین‌دستی‌ها یک‌بار خرید کنند</span>
                </h4>
                <div className="chain-earning-summary-row">
                    {nodeEarnings.map((earnings, index) => (
                        <div className="chain-earning-summary-item" key={`earnings-${index}`}>
                            <strong>گره {nodeLabel(index)}</strong>
                            <span>{formatRial(earnings.totalAmount)}</span>
                        </div>
                    ))}
                </div>
            </section>

            <section className="chain-detail" aria-label="جزئیات درآمد و خرید">
                {view !== 'none' && (
                    <div className="chain-detail-tabs" role="tablist" aria-label="جزئیات درآمد و خرید">
                        {view === 'multi' ? (
                            <button
                                id={multiTabId}
                                type="button"
                                role="tab"
                                aria-selected="true"
                                aria-controls={panelId}
                            >
                                درآمد هر جایگاه از خریدهای انتخاب‌شده
                            </button>
                        ) : (
                            <>
                                <button
                                    id={buyerTabId}
                                    type="button"
                                    role="tab"
                                    aria-selected={visibleTab === 'buyer'}
                                    aria-controls={panelId}
                                    onClick={(event) => selectTab(event, 'buyer')}
                                >
                                    اگر گره {currentLabel} خرید کند
                                </button>
                                <button
                                    id={earningsTabId}
                                    type="button"
                                    role="tab"
                                    aria-selected={visibleTab === 'earnings'}
                                    aria-controls={panelId}
                                    onClick={(event) => selectTab(event, 'earnings')}
                                >
                                    گره {currentLabel} چه درآمدی دارد
                                </button>
                            </>
                        )}
                    </div>
                )}
                <div
                    id={panelId}
                    className="chain-detail-panel"
                    role={view === 'none' ? undefined : 'tabpanel'}
                    aria-labelledby={view === 'none'
                        ? undefined
                        : view === 'multi'
                            ? multiTabId
                            : visibleTab === 'buyer' ? buyerTabId : earningsTabId}
                    tabIndex={0}
                >
                    <div
                        key={`${view}-${singleBuyer}-${visibleTab}-${buyers.join('-')}`}
                        className="chain-detail-content"
                        data-animate={animateDetails ? 'true' : 'false'}
                    >
                        {view === 'none' && (
                            <p className="chain-detail-empty">
                                هنوز خریداری انتخاب نشده است. روی یک یا چند گره کلیک
                                کنید تا درآمد هر جایگاه از این خریدها نمایش داده شود.
                            </p>
                        )}
                        {view === 'multi' && <MultiBuyerPanel data={multi} />}
                        {view === 'single' && visibleTab === 'buyer' && (
                            <>
                                <h4>
                                    اگر گره {currentLabel} با مبلغ {formatRial(salesAmount)} خرید کند
                                </h4>
                                <BuyerDetails buyerIndex={singleBuyer} payouts={buyerPayouts} />
                            </>
                        )}
                        {view === 'single' && visibleTab === 'earnings' && (
                            <>
                                <h4>گره {currentLabel} چه درآمدی دارد</h4>
                                <NodeEarningsDetails
                                    nodeIndex={singleBuyer}
                                    earnings={nodeEarnings[singleBuyer]}
                                />
                            </>
                        )}
                    </div>
                </div>
            </section>
        </section>
    );
}